/**
 * MaterialsOrderListModal — Iter229
 *
 * Aggregates every Ridge / Valley / Barge / Flashing component_line on
 * the appraisal through the 9 m mill max + 200 mm lap rule, then groups
 * identical sticks so the office gets a single supplier-ready order
 * list. Skips Sheet rows by design — sheets have their own length rules.
 *
 * Why this exists: previously the office had to manually count every
 * piece for each pin (10.5 m ridge → "2 × 5.45 m"; 24 m ridge → "3 ×
 * 8.20 m"). Now one button rolls the entire job into a copy-pasteable
 * order list.
 *
 * Backend: GET /api/appraisals/{id}/materials-order-list
 */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { X, Copy, Check, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';

export default function MaterialsOrderListModal({ api, appraisalId, open, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    if (!api || !appraisalId) return;
    setLoading(true);
    try {
      const r = await api.get(`/appraisals/${appraisalId}/materials-order-list`);
      setData(r.data || null);
    } catch (e) {
      toast.error(`Could not load order list: ${e?.response?.data?.detail || e.message}`);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [api, appraisalId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  // Iter229h — separate PENDING rows (Finish barge + on-site-measure
  // flashings) into their own section that surfaces above the regular
  // groups so the office sees outstanding-measurement items first.
  const pendingRows = useMemo(() => (data?.rows || []).filter((r) => r.pending), [data]);
  const readyRows   = useMemo(() => (data?.rows || []).filter((r) => !r.pending), [data]);

  const grouped = useMemo(() => {
    const out = {};
    readyRows.forEach((r) => {
      const key = r.component_type || 'Other';
      out[key] = out[key] || [];
      out[key].push(r);
    });
    return out;
  }, [readyRows]);

  // Build the supplier-friendly plain-text order list. Format:
  //
  //   RIDGE
  //   ─────────────────────────────────────
  //   3 × 8.20 m  Ridge Galv (SE) · RoundTop Std  (R2)
  //   2 × 5.45 m  Ridge Galv (SE) · RoundTop Std  (R1)
  //                                          total 35.50 m
  //
  //   VALLEY
  //   …
  const textForClipboard = useMemo(() => {
    if (!data?.rows?.length) return '';
    const lines = [];
    lines.push(`Materials Order List — appraisal ${appraisalId}`);
    lines.push(`Rule: max ${data.rule.max_stick_m.toFixed(1)} m per stick · ${(data.rule.lap_m * 1000).toFixed(0)} mm lap per joint`);
    lines.push('');
    Object.entries(grouped).forEach(([ct, rows]) => {
      lines.push(`${ct.toUpperCase()}`);
      lines.push('─'.repeat(60));
      let groupStock = 0;
      rows.forEach((r) => {
        const label = [r.type_name, r.cap_type, r.linked_template_name, r.material]
          .filter(Boolean).join(' · ');
        const refs = r.sources?.length ? ` (${r.sources.join(', ')})` : '';
        lines.push(`  ${r.pieces_total} × ${r.stock_each_m.toFixed(2)} m  ${label}${refs}`);
        groupStock += r.total_stock_m;
      });
      lines.push(`  ${' '.repeat(40)}total ${groupStock.toFixed(2)} m`);
      lines.push('');
    });
    lines.push('─'.repeat(60));
    lines.push(`TOTAL: ${data.totals.pieces} pieces · ${data.totals.total_stock_m.toFixed(2)} m stock`);
    return lines.join('\n');
  }, [data, grouped, appraisalId]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(textForClipboard);
      setCopied(true);
      toast.success('Order list copied to clipboard');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Clipboard copy failed — your browser may have blocked it.');
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="materials-order-list-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-4xl max-h-[88vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="materials-order-list-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <ClipboardList size={18} className="text-emerald-700" />
            <h2 className="text-sm font-bold text-slate-800">Materials order list</h2>
            {data?.rule && (
              <span className="text-[10px] text-slate-500 ml-2 font-mono-num">
                max <strong>{data.rule.max_stick_m.toFixed(1)} m</strong>/stick · <strong>{(data.rule.lap_m * 1000).toFixed(0)} mm</strong> lap
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copyToClipboard}
              disabled={!data?.rows?.length}
              data-testid="materials-order-list-copy"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />}
              {copied ? 'Copied' : 'Copy to clipboard'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700"
              data-testid="materials-order-list-close"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {loading && (
            <div className="text-sm text-slate-500 py-12 text-center">Loading order list…</div>
          )}
          {!loading && data && data.rows.length === 0 && (
            <div className="text-sm text-slate-500 py-12 text-center" data-testid="materials-order-list-empty">
              No Ridge / Valley / Barge / Flashing rows to order yet.
              Push some pins from the diagram first.
            </div>
          )}
          {!loading && data && data.rows.length > 0 && (
            <div className="space-y-4" data-testid="materials-order-list-rows">
              {/* Iter229h — PENDING section. Finish barges + on-site-
                  measure flashings need on-site dimensioning before
                  they can hit the supplier order. */}
              {pendingRows.length > 0 && (
                <div
                  data-testid="materials-order-list-pending"
                  className="ring-2 ring-amber-300 bg-amber-50/60 rounded p-2.5 space-y-1.5"
                >
                  <div className="flex items-baseline justify-between border-b border-amber-200 pb-1 mb-1">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-amber-800">
                      ⏳ Pending — measure after install
                    </h3>
                    <span className="text-[11px] font-semibold text-amber-800 font-mono-num">
                      {pendingRows.reduce((s, r) => s + r.pieces_total, 0)} pieces · {pendingRows.reduce((s, r) => s + r.total_stock_m, 0).toFixed(2)} m
                    </span>
                  </div>
                  <p className="text-[10px] text-amber-700 italic">
                    These items can't be ordered until the roof is fitted and final widths/lengths are measured on-site.
                  </p>
                  <table className="w-full text-xs">
                    <tbody className="divide-y divide-amber-100">
                      {pendingRows.map((r, idx) => (
                        <tr key={`pending-${idx}`} data-testid={`mol-pending-row-${idx}`}>
                          <td className="py-1 font-semibold font-mono-num text-slate-800">{r.pieces_total} ×</td>
                          <td className="py-1 text-right font-mono-num text-slate-800 pr-3">{r.stock_each_m.toFixed(2)} m</td>
                          <td className="py-1 text-slate-700">
                            <span>{r.type_name}</span>
                            {r.linked_template_name && (
                              <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                                {r.linked_template_name}
                              </span>
                            )}
                            <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 ring-1 ring-amber-300 font-semibold">
                              {r.barge_role === 'finish' && '⏳ Finish barge'}
                              {r.flashing_role === 'on_site_measure' && '⏳ On-site measure'}
                            </span>
                          </td>
                          <td className="py-1 text-[10px] text-slate-500 font-mono-num">
                            {r.sources?.length ? r.sources.join(', ') : '—'}
                          </td>
                          <td className="py-1 text-right font-mono-num text-slate-800">{r.total_stock_m.toFixed(2)} m</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {Object.entries(grouped).map(([ct, rows]) => {
                const groupStock = rows.reduce((s, r) => s + r.total_stock_m, 0);
                return (
                  <div key={ct} data-testid={`mol-group-${ct.toLowerCase()}`}>
                    <div className="flex items-baseline justify-between border-b border-slate-200 pb-1 mb-1.5">
                      <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">{ct}</h3>
                      <span className="text-[11px] font-semibold text-emerald-700 font-mono-num">
                        {rows.reduce((s, r) => s + r.pieces_total, 0)} pieces · {groupStock.toFixed(2)} m
                      </span>
                    </div>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-[10px] uppercase tracking-wider text-slate-500">
                          <th className="text-left font-semibold py-1">Qty</th>
                          <th className="text-right font-semibold py-1 pr-3">Each</th>
                          <th className="text-left font-semibold py-1">Type / Profile</th>
                          <th className="text-left font-semibold py-1">Refs</th>
                          <th className="text-right font-semibold py-1">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {rows.map((r, idx) => (
                          <tr key={`${ct}-${idx}`} data-testid={`mol-row-${ct.toLowerCase()}-${idx}`} className={r.pending ? 'bg-amber-50/40' : ''}>
                            <td className="py-1 font-semibold font-mono-num text-slate-800">{r.pieces_total} ×</td>
                            <td className="py-1 text-right font-mono-num text-slate-800 pr-3">{r.stock_each_m.toFixed(2)} m</td>
                            <td className="py-1 text-slate-700">
                              <span>{r.type_name}</span>
                              {r.cap_type && <span className="text-slate-500"> · {r.cap_type}</span>}
                              {r.linked_template_name && (
                                <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200">
                                  {r.linked_template_name}
                                </span>
                              )}
                              {/* Iter229d — Barge role chip. Starter
                                  rolls into the supplier PO with the
                                  initial sheets; Finish (PENDING) can
                                  only be ordered after sheets are
                                  installed + final width measured. */}
                              {r.barge_role === 'starter' && (
                                <span
                                  className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300 font-semibold"
                                  data-testid={`mol-row-barge-starter-${idx}`}
                                  title="Starter barge — standard sheet cover width, orderable upfront"
                                >Starter</span>
                              )}
                              {r.flashing_role === 'pre_order' && (
                                <span
                                  className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300 font-semibold"
                                  data-testid={`mol-row-flashing-preorder-${idx}`}
                                  title="Pre-order with initial delivery"
                                >Pre-order</span>
                              )}
                              {r.barge_role === 'finish' && (
                                <span
                                  className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 ring-1 ring-amber-300 font-semibold"
                                  data-testid={`mol-row-barge-finish-${idx}`}
                                  title="Finish barge — custom width, measure on-site after sheets installed"
                                >⏳ Finish · PENDING</span>
                              )}
                              {r.material && (
                                <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] bg-sky-50 text-sky-700 ring-1 ring-sky-200">
                                  {r.material}
                                </span>
                              )}
                            </td>
                            <td className="py-1 text-[10px] text-slate-500 font-mono-num">
                              {r.sources?.length ? r.sources.join(', ') : '—'}
                            </td>
                            <td className="py-1 text-right font-mono-num text-slate-800">{r.total_stock_m.toFixed(2)} m</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {data?.totals && (
          <div className="px-5 py-2 border-t border-slate-200 bg-slate-50 flex items-center justify-between text-xs">
            <div className="text-slate-500">
              Excludes Sheet rows — sheet length rules differ by roof pitch + truss spans.
            </div>
            <div className="font-mono-num">
              <span className="text-slate-600">{data.totals.rows} group{data.totals.rows === 1 ? '' : 's'} · </span>
              <strong className="text-emerald-700">{data.totals.pieces} pieces</strong>
              <span className="text-slate-500"> · </span>
              <strong className="text-slate-800">{data.totals.total_stock_m.toFixed(2)} m total stock</strong>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
